if (!customElements.get('taia-stack-builder')) {
  customElements.define(
    'taia-stack-builder',
    class TaiaStackBuilder extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';
        this.error = this.querySelector('[data-taia-stack-error]');

        this.selectionItems = Array.from(this.querySelectorAll('[data-taia-stack-item]'));
        this.selectionItems.forEach((item) => {
          const button = item.querySelector('[data-taia-stack-select]');
          if (!button) return;
          button.addEventListener('click', () => this.toggleSelection(item, button));
        });

        this.addSelectedButton = this.querySelector('[data-taia-add-selected]');
        if (this.addSelectedButton) {
          this.addSelectedButton.addEventListener('click', () => {
            const items = this.selectedItems().map((item) => ({
              id: item.dataset.variantId,
              quantity: 1,
            }));
            this.addItems(items, this.addSelectedButton);
          });
        }

        this.updateEstimate();

        this.querySelectorAll('[data-taia-add]').forEach((button) => {
          button.addEventListener('click', () => this.addItems([{ id: button.dataset.taiaAdd, quantity: 1 }], button));
        });

        const addAll = this.querySelector('[data-taia-add-all]');
        if (addAll) {
          addAll.addEventListener('click', () => {
            const items = addAll.dataset.taiaAddAll
              .split(',')
              .filter(Boolean)
              .map((id) => ({ id, quantity: 1 }));
            this.addItems(items, addAll);
          });
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            window.setTimeout(() => this.syncFromCartDrawer(), 80);
          });
        }
      }

      toggleSelection(item, button) {
        const selected = item.dataset.taiaSelected !== 'true';
        item.dataset.taiaSelected = String(selected);
        item.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
        button.textContent = selected ? this.dataset.selectedLabel : this.dataset.selectLabel;
        this.updateEstimate();
      }

      selectedItems() {
        return this.selectionItems.filter((item) => item.dataset.taiaSelected === 'true');
      }

      updateEstimate() {
        const subtotalElement = this.querySelector('[data-taia-stack-subtotal]');
        if (!subtotalElement) return;

        const selected = this.selectedItems();
        const prices = selected.map((item) => Number(item.dataset.price || 0));
        const target = Number(this.dataset.target || 4);
        const subtotal = prices.reduce((sum, price) => sum + price, 0);
        const freeQuantity = Math.floor(target / 2);
        const savings = selected.length >= target
          ? prices.slice().sort((a, b) => a - b).slice(0, freeQuantity).reduce((sum, price) => sum + price, 0)
          : 0;
        const total = subtotal - savings;
        const savingsElement = this.querySelector('[data-taia-stack-savings]');
        const totalElement = this.querySelector('[data-taia-stack-total]');
        const messageElement = this.querySelector('[data-taia-stack-selection-message]');
        const dealElement = this.querySelector('[data-taia-stack-deal]');
        const percentElement = this.querySelector('[data-taia-stack-percent]');
        const saveCopyElement = this.querySelector('[data-taia-stack-save-copy]');
        const discountPercent = subtotal > 0 ? Math.round((savings / subtotal) * 100) : 0;

        subtotalElement.textContent = this.formatMoney(subtotal);
        if (savingsElement) savingsElement.textContent = `−${this.formatMoney(savings)}`;
        if (totalElement) totalElement.textContent = this.formatMoney(total);
        if (dealElement) dealElement.hidden = savings === 0;
        if (percentElement) {
          percentElement.textContent = this.dataset.savePercentageLabel.replace('[percent]', discountPercent);
        }
        if (saveCopyElement) {
          saveCopyElement.textContent = this.dataset.youSaveLabel.replace('[amount]', this.formatMoney(savings));
        }

        if (messageElement) {
          messageElement.textContent = selected.length >= target
            ? this.dataset.offerUnlockedLabel
            : this.dataset.offerLockedLabel;
        }

        if (!this.addSelectedButton) return;
        const remaining = Math.max(0, target - selected.length);
        this.addSelectedButton.disabled = remaining > 0;
        const label = this.addSelectedButton.querySelector('span') || this.addSelectedButton;
        if (remaining === 0) {
          label.textContent = this.dataset.addStackLabel;
        } else if (remaining === 1) {
          label.textContent = this.dataset.selectMoreOne;
        } else {
          label.textContent = this.dataset.selectMoreMany.replace('[count]', remaining);
        }
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        }).format(cents / 100);
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      async addItems(items, button) {
        if (!items.length || button.getAttribute('aria-busy') === 'true') return;
        const original = button.innerHTML;
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
        button.textContent = 'Adding...';
        this.showError('');

        try {
          const drawer = document.querySelector('cart-drawer');
          const sectionIds = drawer ? drawer.getSectionsToRender().map((section) => section.id) : [];
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items,
              sections: sectionIds,
              sections_url: window.location.pathname,
            }),
          });
          const data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this piece.');

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'taia-stack-builder',
              productVariantId: items[0].id,
              cartData: data,
            });
          }

          if (drawer && data.sections) {
            drawer.setActiveElement(button);
            drawer.renderContents(data);
          } else {
            window.location.assign(window.routes?.cart_url || '/cart');
          }
          window.setTimeout(() => this.syncFromCartDrawer(), 120);
        } catch (error) {
          this.showError(error.message || 'Unable to add this piece. Please try again.');
        } finally {
          button.removeAttribute('aria-busy');
          button.disabled = false;
          button.innerHTML = original;
        }
      }

      syncFromCartDrawer() {
        const progress = document.querySelector('cart-drawer .taia-cart-progress');
        if (!progress) return;
        const count = Number(progress.dataset.eligibleQuantity || 0);
        const target = Number(progress.dataset.target || this.dataset.target || 4);
        const capped = Math.min(count, target);
        const countEl = this.querySelector('[data-taia-stack-count]');
        const bar = this.querySelector('[data-taia-stack-bar]');
        const message = this.querySelector('[data-taia-stack-message]');
        if (countEl) countEl.textContent = `${capped}/${target}`;
        if (bar) bar.style.width = `${Math.min(100, (count / target) * 100)}%`;
        if (message) message.textContent = this.messageFor(count, target);
      }

      messageFor(count, target) {
        if (count >= target) return 'Your stack is complete.';
        if (count === 3 && target === 4) return 'One more piece unlocks your offer.';
        if (count === 2 && target === 4) return "You're halfway there.";
        if (count === 1 && target === 4) return 'Add 3 more to unlock Buy 2 Get 2.';
        return `Add ${target - count} more eligible pieces to complete your stack.`;
      }

      showError(message) {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = !message;
      }
    }
  );
}
